import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  DecisionTree,
  DefinitionPane,
  Outcome,
  PaneType,
  PaneTypes,
  ProcessedClause,
  RulePane,
  TruthValue,
} from "@financial-advisory/data-access/api-types";
import {
  selectActiveClause,
  setActiveClauseById,
  useGetGuardrailEditDataByDocumentQuery,
  useGetHighlightsQuery,
  useGetProcessedClausesQuery,
} from "@financial-advisory/data-access/financial-advisory-api-service";
import {
  QuestionAnswer,
  RegulationsView,
  type TriStatePanes,
} from "@targeted-support/ui/features/overview";
import { UpdatedNodeInfo, UpdateType } from "@unlikelyai-magic/shared/components";

/**
 * Converts a TruthValue from the API to an Outcome enum value.
 */
const truthValueToOutcome = (truthValue: TruthValue): Outcome => {
  switch (truthValue) {
    case "True":
      return Outcome.TRUE;
    case "False":
      return Outcome.FALSE;
    case "DontKnow":
      return Outcome.UNKNOWN;
    default:
      return Outcome.UNDECIDED;
  }
};

/**
 * Recursively traverses a condition tree to build a mapping from node name (fact ID) to question text.
 */
const buildNameToQuestionMap = (
  node: any,
  map: Map<string, string>
): void => {
  if (!node) return;

  if (node.name && node.question) {
    map.set(node.name, node.question);
  }

  const subConditions = node.subConditions ?? node.sub_conditions ?? [];
  for (const child of subConditions) {
    buildNameToQuestionMap(child, map);
  }
};

/**
 * Extracts question answers from panes for displaying node evaluations.
 */
const extractQuestionAnswers = (panes: PaneTypes[]): QuestionAnswer[] => {
  const rulePanes = panes.filter(
    (pane) => pane.paneType === PaneType.RULE
  ) as RulePane[];

  const questionAnswers: QuestionAnswer[] = [];

  for (const rulePane of rulePanes) {
    const evaluatedTrigger = rulePane.rule.evaluatedTrigger;
    if (!evaluatedTrigger) continue;

    const { assignment, explanations, rootNode } = evaluatedTrigger;

    const nameToQuestion = new Map<string, string>();
    buildNameToQuestionMap(rootNode, nameToQuestion);

    for (const [factId, truthValue] of Object.entries(assignment)) {
      const questionText = nameToQuestion.get(factId);
      if (questionText) {
        questionAnswers.push({
          question: questionText,
          value: truthValueToOutcome(truthValue),
          reasoning: explanations?.[factId],
        });
      }
    }
  }

  return questionAnswers;
};

// Types for multi-clause edit data support
interface ClauseEditData {
  processedClauseId: string;
  editPanes: { ingested: PaneTypes[]; edit: PaneTypes[]; final: PaneTypes[] };
}

interface ClauseUpdatedNodes {
  processedClauseId: string;
  editUpdatedNodes: UpdatedNodeInfo[];
}

interface GuardrailContentProps {
  /** Document name without extension (e.g., "COBS4", "PERG") */
  documentName: string;
  /** Full guardrail name (e.g., "COBS 4.2.1") */
  guardrailName: string;
}

/**
 * Helper to extract processedClauseId from a pane's source
 */
const getProcessedClauseId = (pane: PaneTypes): string | undefined => {
  const source = (pane as DefinitionPane)?.definition?.source ||
    (pane as RulePane)?.rule?.source;
  return (source as any)?.id;
};

export const GuardrailContent = ({
  documentName,
  guardrailName,
}: GuardrailContentProps) => {
  const dispatch = useDispatch();

  // Fetch edit data for the entire document
  const { data: allEditData, isLoading: isEditDataLoading } =
    useGetGuardrailEditDataByDocumentQuery(
      { documentName },
      { skip: !documentName }
    );

  // Process ALL guardrails and build arrays of edit data and updated nodes
  const { clauseEditDataList, clauseUpdatedNodesList, initialProcessedClauseId, initialScrollToPage } = useMemo(() => {
    if (!allEditData?.data) {
      return {
        clauseEditDataList: [],
        clauseUpdatedNodesList: [],
        initialProcessedClauseId: undefined,
        initialScrollToPage: undefined,
      };
    }

    const editDataList: ClauseEditData[] = [];
    const updatedNodesList: ClauseUpdatedNodes[] = [];
    let firstProcessedClauseId: string | undefined;
    let firstScrollToPage: number | undefined;

    for (const [gName, editData] of Object.entries(allEditData.data)) {
      if (!editData?.ingestedTrees?.[0]) continue;

      const firstPane = editData.ingestedTrees[0];
      const processedClauseId = getProcessedClauseId(firstPane);
      if (!processedClauseId) continue;

      const editPane: PaneTypes = {
        paneType: PaneType.DEFINITION,
        definition: {
          rootNode: editData.annotatedTree.tree as unknown as DecisionTree["rootNode"],
          name: gName + " (Annotated)",
          description: "",
        } as DecisionTree,
        id: `${gName}-edit`,
        explanation: "",
        excerpt: "",
      };

      const finalPane: PaneTypes = {
        paneType: PaneType.DEFINITION,
        definition: {
          rootNode: editData.guardrailTree as unknown as DecisionTree["rootNode"],
          name: gName,
          description: "",
        } as DecisionTree,
        id: `${gName}-final`,
        explanation: "",
        excerpt: "",
      };

      editDataList.push({
        processedClauseId,
        editPanes: {
          ingested: editData.ingestedTrees,
          edit: [editPane],
          final: [finalPane],
        },
      });

      if (editData.annotatedTree?.updatedNodes) {
        const updatedNodes = editData.annotatedTree.updatedNodes.map(
          (node: any): UpdatedNodeInfo => ({
            nodeName: node.name,
            updateType: node.updateType as UpdateType,
            explanation: node.explanation,
            note: node.note,
            original: node.original,
            rephrased: node.rephrased,
            sourceNode: node.sourceNode,
            mergedWith: node.mergedWith,
            splitText: node.splitText,
          })
        );
        updatedNodesList.push({
          processedClauseId,
          editUpdatedNodes: updatedNodes,
        });
      }

      if (gName === guardrailName || !firstProcessedClauseId) {
        const source = (firstPane as DefinitionPane)?.definition?.source ||
          (firstPane as RulePane)?.rule?.source;

        if (gName === guardrailName) {
          firstProcessedClauseId = processedClauseId;
          firstScrollToPage = source?.highlights?.[0]?.pageNumber;
        } else if (!firstProcessedClauseId) {
          firstProcessedClauseId = processedClauseId;
          firstScrollToPage = source?.highlights?.[0]?.pageNumber;
        }
      }
    }

    return {
      clauseEditDataList: editDataList,
      clauseUpdatedNodesList: updatedNodesList,
      initialProcessedClauseId: firstProcessedClauseId,
      initialScrollToPage: firstScrollToPage,
    };
  }, [allEditData, guardrailName]);

  // Derive regulationId from documentName
  const regulationId = documentName + ".pdf";

  // Get active clause from Redux (updated by TextHighlight clicks)
  const activeClause: ProcessedClause | undefined = useSelector(selectActiveClause);

  // Fetch processed clauses (populates Redux with highlight-to-clause mappings)
  const { isLoading: isLoadingClauses } = useGetProcessedClausesQuery();

  // Fetch highlights from ingestion service
  const { data: highlightsResponse, isLoading: isLoadingHighlights } =
    useGetHighlightsQuery({ regulationId }, { skip: !regulationId });

  // Track previous regulationId to detect document changes
  const prevRegulationIdRef = useRef<string | undefined>(undefined);

  // Set activeClause based on initialProcessedClauseId or reset when document changes
  useEffect(() => {
    const regulationIdChanged = prevRegulationIdRef.current !== undefined &&
      prevRegulationIdRef.current !== regulationId;
    prevRegulationIdRef.current = regulationId;

    if (regulationIdChanged && !initialProcessedClauseId) {
      dispatch(setActiveClauseById(null));
    } else if (initialProcessedClauseId) {
      dispatch(setActiveClauseById(initialProcessedClauseId));
    }
  }, [dispatch, regulationId, initialProcessedClauseId]);

  // Use highlights from the query (enables click-to-select behavior)
  const highlights = highlightsResponse?.data ?? [];

  // Build Maps for quick lookup of edit data by clauseId
  const editDataByClauseId = useMemo(() =>
    new Map(clauseEditDataList?.map(item => [item.processedClauseId, item.editPanes]) ?? []),
    [clauseEditDataList]
  );

  const updatedNodesByClauseId = useMemo(() =>
    new Map(clauseUpdatedNodesList?.map(item => [item.processedClauseId, item.editUpdatedNodes]) ?? []),
    [clauseUpdatedNodesList]
  );

  // Build triStatePanes based on activeClause and whether it has edit data
  const triStatePanes = useMemo((): TriStatePanes | undefined => {
    if (!activeClause) return undefined;

    const clauseEditPanes = editDataByClauseId.get(activeClause.id);
    if (clauseEditPanes) {
      return {
        ingested: clauseEditPanes.ingested,
        edit: clauseEditPanes.edit,
        final: clauseEditPanes.final,
      };
    }

    return {
      ingested: activeClause.panes,
      edit: activeClause.panes,
      final: activeClause.panes,
    };
  }, [activeClause, editDataByClauseId]);

  // Get editUpdatedNodes for the current activeClause
  const currentEditUpdatedNodes = useMemo(() => {
    if (!activeClause) return undefined;
    return updatedNodesByClauseId.get(activeClause.id);
  }, [activeClause, updatedNodesByClauseId]);

  // Determine if we should show the graph (either editPanes or activeClause)
  const hasGraphData = !!triStatePanes;

  // Check if the current active clause has edit data
  const hasEditData = useMemo(() => {
    if (!activeClause) return false;
    return editDataByClauseId.has(activeClause.id);
  }, [activeClause, editDataByClauseId]);

  // Extract question answers for node evaluations from the current active panes
  const questionAnswers = useMemo(() => {
    const activePanes = triStatePanes?.ingested ?? [];
    return extractQuestionAnswers(activePanes);
  }, [triStatePanes]);

  if (isEditDataLoading || isLoadingClauses || isLoadingHighlights) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-sm text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <RegulationsView
      key={regulationId}
      documentName={regulationId}
      highlights={highlights}
      triStatePanes={triStatePanes}
      panes={triStatePanes?.ingested}
      questionAnswers={questionAnswers}
      hasActiveClause={hasGraphData}
      editUpdatedNodes={currentEditUpdatedNodes}
      initialScrollToPage={initialScrollToPage}
      hasEditData={hasEditData}
    />
  );
};
